import { NATIVE_TOKEN } from './chunk-TIUGIO3J.js';

// src/funds/moves.ts
var VALUE_SCENE_KINDS = Object.freeze(["flow", "interaction"]);
var STRUCTURAL_EDGE_KINDS = /* @__PURE__ */ new Set(["call", "contains", "creates", "verify"]);
function num(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string" || !value.trim()) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}
function lower(value) {
  return value ? value.toLowerCase() : null;
}
function partyOf(node, fallbackId) {
  return lower(node?.address) ?? fallbackId;
}
function assetOf(edge, txChain) {
  const a = edge.asset;
  if (!a) return null;
  const chainId = a.token_chain_id ?? edge.from_chain ?? txChain ?? null;
  const tokenAddress = lower(a.token_address);
  return {
    key: `${chainId ?? ""}:${tokenAddress ?? NATIVE_TOKEN}`,
    symbol: a.symbol ?? null,
    chainId,
    tokenAddress,
    decimals: a.decimals ?? null
  };
}
function amountOf(edge) {
  const a = edge.asset;
  if (!a) return null;
  const display = num(a.amount);
  if (display !== null) return display;
  const raw = num(a.amount_raw);
  if (raw === null || a.decimals == null) return null;
  return raw / 10 ** a.decimals;
}
function isObserved(edge) {
  return (edge.origin ?? "decoded") === "decoded" && !edge.virtual;
}
function isValueScene(scene) {
  return !!scene && VALUE_SCENE_KINDS.includes(String(scene.kind));
}
function unreadableMoves(scene, txChain) {
  if (!isValueScene(scene)) return 0;
  const nodes = new Map(scene.nodes.map((n) => [n.id, n]));
  let lost = 0;
  for (const edge of scene.edges) {
    if (!isObserved(edge) || STRUCTURAL_EDGE_KINDS.has(String(edge.kind))) continue;
    const a = edge.asset;
    if (!a) continue;
    if (partyOf(nodes.get(edge.source), edge.source) === partyOf(nodes.get(edge.target), edge.target)) continue;
    const amount = amountOf(edge);
    if (amount !== null && amount !== 0 && assetOf(edge, txChain)) continue;
    if (amount === 0) continue;
    if (a.amount == null && a.amount_raw == null) continue;
    lost += 1;
  }
  return lost;
}
function movesFromScene(scene, txId, txChain) {
  if (!isValueScene(scene)) return [];
  const nodes = new Map(scene.nodes.map((n) => [n.id, n]));
  const out = [];
  for (const edge of scene.edges) {
    if (!isObserved(edge) || STRUCTURAL_EDGE_KINDS.has(String(edge.kind))) continue;
    const asset = assetOf(edge, txChain);
    if (!asset) continue;
    const amount = amountOf(edge);
    if (amount === null || amount === 0) continue;
    const from = partyOf(nodes.get(edge.source), edge.source);
    const to = partyOf(nodes.get(edge.target), edge.target);
    if (from === to) continue;
    out.push({
      txId,
      chainId: edge.from_chain ?? nodes.get(edge.source)?.chain_id ?? txChain ?? null,
      from,
      to,
      asset,
      amount,
      usd: num(edge.asset?.usd)
    });
  }
  return out;
}
function chainOf(node, edgeChain) {
  return node?.chain_id ?? edgeChain ?? null;
}
var BRIDGE_EDGE_KINDS = /* @__PURE__ */ new Set(["bridge_out", "bridge_in", "cross_chain"]);
function bridgeNameFromScene(scene) {
  if (!scene) return null;
  for (const node of scene.nodes) {
    if (!node.off_chain) continue;
    if (String(node.kind ?? "").toLowerCase() !== "bridge") continue;
    return typeof node.label === "string" ? node.label.trim() : "";
  }
  const nodes = new Map(scene.nodes.map((n) => [n.id, n]));
  for (const edge of scene.edges) {
    if (!BRIDGE_EDGE_KINDS.has(String(edge.kind ?? "").toLowerCase())) continue;
    for (const end of [edge.target, edge.source]) {
      const label = nodes.get(end)?.label;
      if (typeof label === "string" && label.trim()) return label.trim();
    }
    return "";
  }
  return null;
}
function bridgeHopsFromScene(scene, txId, txChain) {
  if (!scene) return [];
  const local = txChain;
  const nodes = new Map(scene.nodes.map((n) => [n.id, n]));
  const isLocal = (chain) => !!chain && !!local && chain === local;
  const isRemote = (chain) => !!chain && (!local || chain !== local);
  const hops = /* @__PURE__ */ new Map();
  const add = (hop) => {
    const key = `${hop.direction}|${hop.localParty ?? ""}|${hop.remoteChain ?? ""}`;
    if (!hops.has(key)) hops.set(key, hop);
  };
  for (const bridge of scene.nodes) {
    if (!bridge.off_chain) continue;
    const into = scene.edges.filter((e) => e.target === bridge.id);
    const outOf = scene.edges.filter((e) => e.source === bridge.id);
    for (const e1 of into) {
      const srcNode = nodes.get(e1.source);
      const srcChain = chainOf(srcNode, e1.from_chain);
      for (const e2 of outOf) {
        const dstNode = nodes.get(e2.target);
        const dstChain = chainOf(dstNode, e2.to_chain);
        const valueEdge = e1.asset ? e1 : e2;
        const base = {
          txId,
          localChain: local,
          protocol: bridge.label || null,
          asset: assetOf(valueEdge, local),
          amount: amountOf(valueEdge),
          usd: num(valueEdge.asset?.usd)
        };
        if (isLocal(srcChain) && isRemote(dstChain)) {
          add({
            ...base,
            direction: "out",
            remoteChain: dstChain,
            localParty: srcNode?.address ? lower(srcNode.address) : null,
            remoteParty: lower(dstNode?.address),
            expected: !isObserved(e2)
          });
        } else if (isRemote(srcChain) && isLocal(dstChain)) {
          add({
            ...base,
            direction: "in",
            remoteChain: srcChain,
            localParty: dstNode?.address ? lower(dstNode.address) : null,
            remoteParty: lower(srcNode?.address),
            expected: !isObserved(e1)
          });
        }
      }
    }
  }
  for (const edge of scene.edges) {
    if (!edge.cross_chain) continue;
    const from = edge.from_chain ?? null;
    const to = edge.to_chain ?? null;
    const src = nodes.get(edge.source);
    const dst = nodes.get(edge.target);
    if (src?.off_chain || dst?.off_chain) continue;
    const base = {
      txId,
      localChain: local,
      protocol: null,
      asset: assetOf(edge, local),
      amount: amountOf(edge),
      usd: num(edge.asset?.usd),
      expected: !isObserved(edge)
    };
    if (isLocal(from) && isRemote(to)) {
      add({ ...base, direction: "out", remoteChain: to, localParty: lower(src?.address), remoteParty: lower(dst?.address) });
    } else if (isRemote(from) && isLocal(to)) {
      add({ ...base, direction: "in", remoteChain: from, localParty: lower(dst?.address), remoteParty: lower(src?.address) });
    }
  }
  return [...hops.values()];
}
function readTransactionMoney(input) {
  const moves = movesFromScene(input.valueScene, input.txId, input.chainId);
  let hops = bridgeHopsFromScene(input.valueScene, input.txId, input.chainId);
  if (hops.length === 0 && input.semanticScene) {
    hops = bridgeHopsFromScene(input.semanticScene, input.txId, input.chainId);
  }
  return {
    moves,
    hops,
    hasValueScene: isValueScene(input.valueScene),
    // A value scene can be present and still yield nothing — see
    // `unreadableMoves`. `hasValueScene` alone reads as "decoded, and it moved
    // nothing", which is a different claim.
    unreadable: unreadableMoves(input.valueScene, input.chainId)
  };
}
function focusMoves(moves, focus) {
  if (focus.size === 0 || moves.length === 0) return moves;
  const touching = moves.filter((move) => focus.has(move.from) || focus.has(move.to));
  return touching.length > 0 ? touching : moves;
}
function transitParties(legs, focus) {
  const out = /* @__PURE__ */ new Set();
  if (legs.length < 2 || focus.size === 0) return out;
  for (const leg of legs) {
    const far = leg.moves.filter((move) => focus.has(move.from) !== focus.has(move.to)).map((move) => focus.has(move.from) ? move.to : move.from);
    if (far.length === 0) return /* @__PURE__ */ new Set();
    for (const party of far) out.add(party);
  }
  return out;
}

export { VALUE_SCENE_KINDS, bridgeHopsFromScene, bridgeNameFromScene, focusMoves, isValueScene, movesFromScene, readTransactionMoney, transitParties, unreadableMoves };
//# sourceMappingURL=chunk-7II7PHUH.js.map
//# sourceMappingURL=chunk-7II7PHUH.js.map